import { Events, Fields, Versions } from '@vimeo/bigpicture-client';
import { getPageNameFromPath } from '@vimeo/ui/core/F2PJoinLogin/utils';
import getDeviceType from '@vimeo/ui/core/utils/getDeviceType';
import {
  ActionBpContext,
  buildActionBpContext,
  buildProductAnalyticsBpContext,
  buildTeamBpContext,
  buildThirdPartyIntegrationBpContext,
  buildViewBpContext,
  buildWebBpContext,
  ProductAnalyticsBpContext,
  sendBpEventWithContexts,
  TeamBpContext,
  WebBpContext,
} from '@vimeo/use-tracking';
import { TeamUser, useViewer, Viewer } from '@vimeo/viewer';

export interface NavigationEvent {
  actionType?: ActionBpContext['action_type'];
  additionalFields?: Record<string, unknown>;
  copy?: string | null;
  element?: ProductAnalyticsBpContext['element'];
  entityType?: ProductAnalyticsBpContext['entity_type'];
  eventName?: Events;
  feature?: ProductAnalyticsBpContext['feature'];
  flow?: ProductAnalyticsBpContext['flow'];
  product?: ProductAnalyticsBpContext['product'];
  target?: WebBpContext['target'];
  targetPath?: WebBpContext['target_path'];
  version?: number;
  viewType?: 'impression';
}

export interface NavigationEventWithViewer extends NavigationEvent {
  viewer: Viewer;
}

/** combine viewer context and event tracking helper */
export const useTrackNavigationEvent = () => {
  const viewer = useViewer();

  return (args: NavigationEvent) => trackNavigationEvent({ ...args, viewer });
};

export const trackNavigationActionEvent = (args: NavigationEventWithViewer) =>
  trackNavigationEvent({
    ...args,
    actionType: 'click',
  });

export const trackNavigationViewEvent = (args: NavigationEventWithViewer) =>
  trackNavigationEvent({
    ...args,
    viewType: 'impression',
  });

const trackNavigationEvent = ({
  actionType = 'click',
  additionalFields,
  copy = null,
  element,
  entityType,
  eventName = 'vimeo.top_nav_bar_action',
  feature = 'general',
  flow = null,
  product = 'general',
  target = null,
  targetPath = null,
  version,
  viewer,
  viewType,
}: NavigationEventWithViewer) => {
  const user = viewer?.user;
  const teamUser = viewer?.teamUser;
  const pageName: WebBpContext['page_name'] = getPageNameFromPath(window.location.pathname, user);
  const referrerPageName: WebBpContext['referrer_page_name'] = document.referrer
    ? getPageNameFromPath(`/${document.referrer}`, user)
    : null;
  const actionViewBpContext = viewType
    ? buildViewBpContext({ view_type: viewType, feature: null })
    : buildActionBpContext({
        action_type: actionType,
        feature: null,
      });
  const contexts = {
    ...actionViewBpContext,
    ...buildWebBpContext({
      page_name: pageName,
      location: null,
      referrer_page_name: referrerPageName,
      referrer: document.referrer,
      path: window.location.pathname,
      target,
      target_path: targetPath,
      copy,
    }),
    ...buildProductAnalyticsBpContext({
      product,
      feature,
      location: 'top_navigation_bar',
      modal_name: null,
      flow,
      element,
      entity_type: entityType ?? null,
      device_type: getDeviceType(),
      is_user_facing_data: null,
      copy,
    }),
    ...buildTeamBpContextFromTeamUser(teamUser),
    ...buildThirdPartyIntegrationBpContext({
      is_partner: false,
      integration_id: null,
      integration_name: null,
    }),
  };

  const isUpsellEvent = ['vimeo.upsell_trigger_impression', 'vimeo.trigger_upsell'].includes(
    eventName,
  );

  sendBpEventWithContexts(
    eventName,
    contexts,
    version as Versions<Events>,
    { ...additionalFields, ...(isUpsellEvent ? additionalUpsellFields : {}) } as Fields<
      Events,
      Versions<Events>
    >,
  );
};

export const buildTeamBpContextFromTeamUser = (
  teamUser?: TeamUser,
  resource_permission_level?: TeamBpContext['resource_permission_level'],
) => {
  return buildTeamBpContext({
    is_team_member: Boolean(teamUser),
    team_owner_id: teamUser?.ownerId || null,
    team_subscription_type: teamUser?.accountType || null,
    team_role:
      (snakeCase(
        teamUser?.plainTextPermissionLevel?.toLowerCase(),
      ) as TeamBpContext['team_role']) || null,
    team_size: teamUser?.currentTeamSize || null,
    team_id: teamUser?.teamId || null,
    resource_permission_level: resource_permission_level || null,
    joined_team_at: teamUser?.joinedOn ? new Date(teamUser?.joinedOn).getTime() : null,
  });
};

const snakeCase = (string?: string) => {
  if (string) {
    return string
      .replace(/\d+/g, ' ')
      .split(/ |\B(?=[A-Z])/)
      .map((word) => word.toLowerCase())
      .join('_');
  }
};

const additionalUpsellFields = {
  plan_selected: null,
  purchase_type: null,
  duration: null,
  usd_price: null,
  price: null,
  currency: null,
  is_discount: null,
  discount_offer: null,
  promo_code_id: null,
  interface_type: null,
  selected_plan: null,
  add_on_feature: null,
};
